import { useQuery } from '@tanstack/react-query'
import { dashboardConfigApi } from '@/lib/api'
import { BaseWidget } from './BaseWidget'
import { FileText, Users, Receipt } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { RecentInvoice, RecentClient, RecentExpense } from '@/types'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  DRAFT: 'secondary',
  SENT: 'default',
  PAID: 'default',
  OVERDUE: 'destructive',
  CANCELLED: 'outline',
  PARTIALLY_PAID: 'default',
}

// Recent Invoices Widget
export function RecentInvoicesWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'RECENT_INVOICES'],
    queryFn: () => dashboardConfigApi.getWidgetData<RecentInvoice[]>('RECENT_INVOICES'),
  })

  const invoices = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.recentInvoices')}
      icon={<FileText className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="space-y-3">
        {invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noRecentInvoices')}</p>
        ) : (
          invoices.map((invoice) => (
            <Link
              key={invoice.invoice_id}
              to={`/billing/invoices/${invoice.invoice_id}`}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{invoice.invoice_number}</p>
                <p className="text-xs text-muted-foreground truncate">{invoice.client_name}</p>
              </div>
              <div className="text-right ml-2">
                <p className="text-sm font-medium">{formatCurrency(invoice.total_amount, invoice.currency)}</p>
                <Badge variant={STATUS_VARIANTS[invoice.status || 'DRAFT'] || 'secondary'} className="text-xs">
                  {t(`billing.${(invoice.status || 'draft').toLowerCase()}`)}
                </Badge>
              </div>
            </Link>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

// Recent Clients Widget
export function RecentClientsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'RECENT_CLIENTS'],
    queryFn: () => dashboardConfigApi.getWidgetData<RecentClient[]>('RECENT_CLIENTS'),
  })

  const clients = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.recentClients')}
      icon={<Users className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="space-y-3">
        {clients.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noRecentClients')}</p>
        ) : (
          clients.map((client) => (
            <Link
              key={client.client_id}
              to={`/clients/${client.client_id}`}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{client.client_name}</p>
                <p className="text-xs text-muted-foreground truncate">{client.client_email}</p>
              </div>
              <div className="text-right ml-2">
                <p className="text-xs text-muted-foreground">{client.country}</p>
                <p className="text-xs text-muted-foreground">{formatDate(client.created_at)}</p>
              </div>
            </Link>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

// Recent Expenses Widget
export function RecentExpensesWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'RECENT_EXPENSES'],
    queryFn: () => dashboardConfigApi.getWidgetData<RecentExpense[]>('RECENT_EXPENSES'),
  })

  const expenses = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.recentExpenses')}
      icon={<Receipt className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="space-y-3">
        {expenses.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noRecentExpenses')}</p>
        ) : (
          expenses.map((expense) => (
            <Link
              key={expense.expense_id}
              to="/billing/expenses"
              className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{expense.category_name}</p>
                <p className="text-xs text-muted-foreground truncate">{expense.remarks || '-'}</p>
              </div>
              <div className="text-right ml-2">
                <p className="text-sm font-medium">{formatCurrency(expense.amount, expense.currency)}</p>
                <p className="text-xs text-muted-foreground">{formatDate(expense.expense_date)}</p>
              </div>
            </Link>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

